import type { NotificationType } from '@mfw/common';
import type { CustomContentProps } from 'notistack';

import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Iconify } from '@mfw/components';
import { closeSnackbar } from 'notistack';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import React from 'react';

import type { UiNotification } from '../../schemas/notification.schema';

import { useSetDailyActivityModal } from '../../store/useDailyActivityModalStore';
import { useSetMembershipCardSetup } from '../../store/useMembershipCardSetupStore';
import { useSetMembershipTouAcceptance } from '../../store/useMembershipTouAcceptanceStore';
import { useSetAccountCardsRequest } from '../../store/useAccountCardsRequestStore';
import { useNavigate } from '../../router/hooks';
import {
  isActionableNotification,
  openNotificationTarget,
} from '../../utils/openNotificationTarget';

declare module 'notistack' {
  interface VariantOverrides {
    notification: {
      notificationType: NotificationType;
      data: UiNotification['data'];
      body: string;
    };
  }
}

// Title and body arrive as the rich text the notification list renders: tags
// AND entities (a name with "&" is stored as "&amp;"). DOMParser decodes the
// entities without running scripts or loading anything.
const toPlainText = (html: string) =>
  (
    new DOMParser().parseFromString(html.replace(/<[^>]*>/g, ' '), 'text/html')
      .body.textContent ?? ''
  )
    .replace(/\s+/g, ' ')
    .trim();

export const NOTIFICATION_ICON: Record<NotificationType, string> = {
  BIRTHDAY_CONGRATULATIONS: 'mdi:cake-variant-outline',
  BIRTHDAY_REMINDER: 'mdi:cake-variant-outline',
  BOOKING_CONFIRMED: 'mdi:calendar-check-outline',
  BOOKING_EVENT_CANCELLED: 'mdi:calendar-remove-outline',
  BOOKING_EVENT_UPDATED: 'mdi:calendar-edit-outline',
  BOOKING_REMINDER: 'mdi:calendar-alert-outline',
  BOOKING_REMINDER_1H: 'mdi:calendar-alert-outline',
  // the "you can still cancel free of charge" reminders — a card/clock glyph
  // rather than the plain calendar alert, since the thing at stake is money
  BOOKING_CANCELLATION_DEADLINE_REMINDER: 'mdi:credit-card-clock-outline',
  BOOKING_CANCELLATION_DEADLINE_REMINDER_1H: 'mdi:credit-card-clock-outline',
  // a place came free and the client is next in line — an invitation to act,
  // hence the clock rather than a plain calendar glyph
  BOOKING_WAITLIST_SEAT_AVAILABLE: 'mdi:calendar-clock-outline',
  CLIENT_LEFT_ORG: 'mdi:account-arrow-right-outline',
  DISCONNECTED_FROM_ORG: 'mdi:account-off-outline',
  INVITATION_ACCEPTED: 'mdi:account-check-outline',
  INVITATION_REJECTED: 'mdi:account-cancel-outline',
  // T12: the payout for a sale actually left the platform — distinct from
  // the cart/refund glyphs above, a check mark on cash rather than a card
  // 6.8: the platform took a listing down and withdrew the seller's access
  MARKETPLACE_ACCESS_DENIED: 'mdi:store-remove-outline',
  MARKETPLACE_PAYOUT_PAID: 'mdi:cash-check',
  MARKETPLACE_PURCHASE_DELIVERED: 'mdi:cart-check',
  MARKETPLACE_PURCHASE_REFUNDED: 'mdi:cash-refund',
  MARKETPLACE_SALE: 'mdi:cart-outline',
  // a trainer saved Terms of Use of their own — a document, edited
  TRAINER_OWN_TERMS_SAVED: 'mdi:file-document-edit-outline',
  // MV2-573: a recurring membership's card kept failing until suspended —
  // same glyph as PAYMENT_FAILED, the staff-facing counterpart of it
  MEMBERSHIP_PAYMENT_SUSPENDED: 'mdi:credit-card-off-outline',
  // MV2-573/Task 11: 7/3/1-day reminders — renewal (still going, informational)
  // vs expiry (ending, needs a decision), distinct glyphs on purpose
  MEMBERSHIP_RENEWAL_REMINDER: 'mdi:calendar-sync-outline',
  MEMBERSHIP_EXPIRY_REMINDER: 'mdi:calendar-alert-outline',
  // Task 11 (membership-renewal plan) — a cardless auto-renewal: a
  // "refresh" calendar, distinct from the card renewal's "sync" one (no card
  // is charged here, the front desk records the payment)
  MEMBERSHIP_AUTO_RENEWAL_REMINDER: 'mdi:calendar-refresh-outline',
  // Task 12 (membership-renewal plan) — the once-a-day owner/front-desk
  // summary: a clipboard glyph, distinct from every per-client reminder
  // above (this one is never about a single client)
  MEMBERSHIP_DAILY_DIGEST: 'mdi:clipboard-text-clock-outline',
  // MV2-573 §4.2: staff undid a check-in — an "undo" arrow, distinct from
  // the check-in itself
  MEMBERSHIP_CHECK_IN_VOIDED: 'mdi:undo-variant',
  // Same event, addressed to the client — same glyph as the staff notice
  MEMBERSHIP_CHECK_IN_VOIDED_FOR_CLIENT: 'mdi:undo-variant',
  // MVP client notices: pause / cancel / manual renewal of a membership
  MEMBERSHIP_PAUSED: 'mdi:pause-circle-outline',
  MEMBERSHIP_CANCELLED: 'mdi:close-circle-outline',
  MEMBERSHIP_RENEWED_MANUALLY: 'mdi:autorenew',
  // a visit taken off the membership by a check-in
  MEMBERSHIP_VISIT_DEDUCTED: 'mdi:ticket-confirmation-outline',
  // a client gave up their own seat — same glyph family as the event cancel
  BOOKING_CANCELLED_BY_CLIENT: 'mdi:account-minus-outline',
  // a client's PAR-Q has a stopping answer: they wait to be let in
  HEALTH_SCREENING_HOLD: 'mdi:heart-alert-outline',
  // Task 10 (membership-renewal plan) — a cycle's visits ran out: a
  // depleted-battery glyph, distinct from every card/payment icon above
  // since nothing to do with money is at stake here
  MEMBERSHIP_VISITS_USED_UP: 'mdi:battery-alert-variant-outline',
  // Task 10 — a client's self-service attempt was refused for having no
  // visits left: same family as the check-in glyph elsewhere in the app,
  // with the "off" mark that pairs with MEMBERSHIP_VISITS_USED_UP above
  MEMBERSHIP_NO_VISITS_LEFT: 'mdi:calendar-remove-outline',
  // MV2-573 §1.2: a scheduled first-charge attempt failed — same glyph as
  // the other card-failure notices
  MEMBERSHIP_SCHEDULED_ACTIVATION_FAILED: 'mdi:credit-card-off-outline',
  // MV2-573 §1: nudges the client to link a card — a "please add" glyph,
  // distinct from the failure/off glyphs above
  MEMBERSHIP_CARD_SETUP_REQUIRED: 'mdi:credit-card-plus-outline',
  // the 72h to link that card ran out and the membership expired
  MEMBERSHIP_CARD_SETUP_EXPIRED: 'mdi:credit-card-clock-outline',
  // a membership's Terms of Use changed and needs re-acceptance — a document
  // glyph, distinct from every card/payment icon above since this is a
  // consent action, not a payment one
  MEMBERSHIP_TOU_UPDATED: 'mdi:file-document-edit-outline',
  NOTIFICATION_CENTER: 'mdi:bell-outline',
  PAYMENT_FAILED: 'mdi:credit-card-off-outline',
  SCHEDULE_EVENT_ADDED: 'mdi:calendar-plus',
  SCHEDULE_EVENT_COMPLETED: 'mdi:calendar-check',
  SCHEDULE_NOTE: 'mdi:calendar-text',
  SUBSCRIPTION_SUSPENDED: 'mdi:account-clock-outline',
  UNLINK_CLIENT_REMOVED_FROM_ORG: 'mdi:account-remove-outline',
  UNLINK_TRAINER_REMOVED_FROM_ORG: 'mdi:account-remove-outline',
  DAILY_ACTIVITY_REMINDER_MORNING: 'mdi:weather-sunset-up',
  DAILY_ACTIVITY_REMINDER_EVENING: 'mdi:weather-sunset-down',
  DAILY_ACTIVITY_SHARED_WITH_TRAINER: 'mdi:share-variant-outline',
};

interface NotificationToastProps extends CustomContentProps {
  notificationType: NotificationType;
  data: UiNotification['data'];
  body: string;
}

export const NotificationToast = React.forwardRef<
  HTMLDivElement,
  NotificationToastProps
>(({ id, body, data, message, notificationType }, ref) => {
  const theme = useTheme();
  const openDailyActivityModal = useSetDailyActivityModal();
  const openMembershipCardSetup = useSetMembershipCardSetup();
  const openMembershipTouAcceptance = useSetMembershipTouAcceptance();
  const openAccountCards = useSetAccountCardsRequest();
  const navigate = useNavigate();
  const openScheduleDay = React.useCallback(
    (date: string) => navigate('/schedule', { search: `date=${date}` }),
    [navigate],
  );
  const icon = NOTIFICATION_ICON[notificationType] ?? 'mdi:bell-outline';
  const isActionable = isActionableNotification(data);

  return (
    <Paper
      sx={{
        alignItems: 'flex-start',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: theme.customShadows.dropdown,
        cursor: isActionable ? 'pointer' : 'default',
        display: 'flex',
        gap: 1.5,
        maxWidth: 380,
        minWidth: 300,
        p: '12px 14px',
        pointerEvents: 'all',
      }}
      onClick={
        isActionable
          ? () => {
              openNotificationTarget(
                data,
                openDailyActivityModal,
                openMembershipCardSetup,
                openMembershipTouAcceptance,
                openAccountCards,
                openScheduleDay,
              );
              closeSnackbar(id);
            }
          : undefined
      }
      elevation={0}
      ref={ref}
    >
      {/* The glyph sits on the `lighter` tint, which is pale on every
          ladder, so it is measured against THAT, not the page: `main` is
          1.5:1 there, `darker` 4.7:1. */}
      <Box
        bgcolor="primary.lighter"
        justifyContent="center"
        color="primary.darker"
        alignItems="center"
        borderRadius="50%"
        flexShrink={0}
        display="flex"
        height={36}
        width={36}
        mt="2px"
      >
        <Iconify size="medium" icon={icon} />
      </Box>

      <Box minWidth={0} flex={1}>
        <Typography
          sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.4 }}
          variant="subtitle2"
          noWrap
        >
          {typeof message === 'string' ? toPlainText(message) : message}
        </Typography>
        <Typography
          sx={{
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            color: 'text.secondary',
            display: '-webkit-box',
            lineHeight: 1.4,
            mt: '2px',
            overflow: 'hidden',
          }}
          variant="body2"
        >
          {toPlainText(body)}
        </Typography>
      </Box>

      <IconButton
        sx={{
          '&:hover': { color: 'text.primary', opacity: 1 },
          color: 'text.secondary',
          flexShrink: 0,
          mr: '-6px',
          mt: '-4px',
          opacity: 0.6,
        }}
        onClick={(e) => {
          e.stopPropagation();
          closeSnackbar(id);
        }}
        size="small"
      >
        <Iconify icon="mdi:close" size="small" />
      </IconButton>
    </Paper>
  );
});

NotificationToast.displayName = 'NotificationToast';
